import { cn } from '@/lib/utils'
import {
  productListItemClass,
  productListLayoutClass,
  shopProductListItemClass,
  shopProductListLayoutClass,
} from '@/store/components/productListLayout'

/** Storefront shimmer block — uses luxury-themed skeleton-shimmer from global CSS. */
export function StoreSkeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton-shimmer', className)} aria-hidden />
}

export function PageHeaderSkeleton({
  withSubtitle = true,
  withBack = true,
}: {
  withSubtitle?: boolean
  withBack?: boolean
}) {
  return (
    <div className="section-gap pt-6 md:pt-8 lg:pt-10">
      <div
        className={cn(
          'grid items-center gap-x-3 sm:gap-x-4',
          withBack
            ? 'grid-cols-[2.5rem_minmax(0,1fr)]'
            : 'grid-cols-1'
        )}
      >
        {withBack && <StoreSkeleton className="!size-10 shrink-0 brand-radius-a" />}
        <div className="min-w-0 space-y-3">
          <StoreSkeleton className="h-9 w-48 max-w-full rounded-lg" />
          {withSubtitle && (
            <StoreSkeleton className="h-4 w-64 max-w-full rounded-md" />
          )}
        </div>
      </div>
    </div>
  )
}

export function SectionHeadingSkeleton({ withLink = true }: { withLink?: boolean }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-3">
        <StoreSkeleton className="h-9 w-48 rounded-lg" />
        <StoreSkeleton className="h-4 w-64 max-w-full rounded-md" />
      </div>
      {withLink && (
        <StoreSkeleton className="h-9 w-28 shrink-0 rounded-xl opacity-90" />
      )}
    </div>
  )
}

/** Matches {@link ProductCard} layout. */
export function ProductCardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('product-card overflow-hidden', className)} aria-hidden>
      <div className="flex flex-row gap-3.5 p-3.5 lg:flex-col lg:gap-0 lg:p-0">
        <div className="shrink-0 lg:overflow-visible">
          <div className="product-card-media relative aspect-square w-[9rem] max-sm:bg-transparent md:w-[8rem] lg:w-full lg:aspect-square">
            <StoreSkeleton className="absolute inset-0 rounded-none opacity-70" />
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 lg:p-3 lg:pb-3">
          <StoreSkeleton className="h-2 w-14 rounded" />
          <StoreSkeleton className="h-4 w-full rounded-md" />
          <StoreSkeleton className="hidden h-3 w-2/3 rounded-md lg:block" />
          <div className="flex items-center justify-between gap-2 pt-0.5">
            <StoreSkeleton className="h-6 w-16 rounded-md" />
            <StoreSkeleton className="h-5 w-14 rounded-full" />
          </div>
        </div>
      </div>
      <div className="product-card-actions product-card-actions-panel mx-3 mb-3 rounded-xl px-2.5 pt-1 pb-2.5 lg:hidden">
        <div className="mb-2.5 flex flex-wrap items-center gap-3">
          <StoreSkeleton className="h-8 w-24 rounded-lg" />
          <StoreSkeleton className="h-8 w-10 rounded-lg" />
          <StoreSkeleton className="h-8 w-10 rounded-lg" />
          <StoreSkeleton className="ml-auto h-5 w-16 rounded-full" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <StoreSkeleton className="h-9 rounded-xl" />
          <StoreSkeleton className="h-9 rounded-xl" />
          <StoreSkeleton className="h-9 rounded-xl" />
        </div>
      </div>
    </div>
  )
}

export function ProductGridSkeleton({
  count = 8,
  className,
  layout = 'carousel',
}: {
  count?: number
  className?: string
  layout?: 'carousel' | 'grid'
}) {
  const listLayoutClass =
    layout === 'grid' ? shopProductListLayoutClass : productListLayoutClass
  const listItemClass =
    layout === 'grid' ? shopProductListItemClass : productListItemClass

  return (
    <div
      className={cn(listLayoutClass, className)}
      aria-busy
      aria-label="Loading products"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={listItemClass}>
          <ProductCardSkeleton />
        </div>
      ))}
    </div>
  )
}

export function ShopPageSkeleton() {
  return (
    <div aria-busy aria-label="Loading shop">
      <PageHeaderSkeleton />
      <div className="flex flex-col gap-8 lg:flex-row">
        <aside className="w-full shrink-0 lg:w-60">
          <div className="glass-card shop-filter-panel overflow-hidden">
            <div className="border-b border-luxury-700/30 px-4 py-3.5">
              <StoreSkeleton className="h-5 w-24 rounded-md" />
              <StoreSkeleton className="mt-2 h-3 w-16 rounded-md" />
              <StoreSkeleton className="shop-filter-mobile-toggle mt-3 h-10 w-full lg:hidden" />
            </div>
            <div className="hidden divide-y divide-luxury-700/25 lg:block">
              <div className="space-y-3 p-4">
                <StoreSkeleton className="h-3 w-14 rounded" />
                <div className="grid grid-cols-2 gap-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <StoreSkeleton key={i} className="h-9 rounded-xl" />
                  ))}
                </div>
              </div>
              <div className="space-y-3 p-4">
                <StoreSkeleton className="h-3 w-20 rounded" />
                <StoreSkeleton className="h-9 w-full rounded-xl" />
              </div>
              <div className="space-y-2 p-4">
                <StoreSkeleton className="h-3 w-16 rounded" />
                {Array.from({ length: 5 }).map((_, i) => (
                  <StoreSkeleton key={i} className="h-9 w-full rounded-lg" />
                ))}
              </div>
            </div>
          </div>
        </aside>
        <div className="flex-1">
          <ProductGridSkeleton count={8} layout="grid" />
        </div>
      </div>
    </div>
  )
}

export function CartPageSkeleton({ withBack = true }: { withBack?: boolean }) {
  return (
    <div className="page-shell pb-36 lg:pb-12" aria-busy aria-label="Loading cart">
      <PageHeaderSkeleton withSubtitle={false} withBack={withBack} />
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-10">
        <div className="min-w-0 space-y-4 sm:space-y-5">
          <StoreSkeleton className="brand-radius-a-lg h-[7.25rem] w-full sm:h-[7.5rem]" />
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="cart-line-card">
              <div className="flex gap-3.5 sm:gap-4">
                <StoreSkeleton className="cart-line-media h-[5.75rem] w-[5.75rem] sm:h-[6.5rem] sm:w-[6.5rem]" />
                <div className="flex flex-1 flex-col">
                  <div className="flex justify-between gap-3">
                    <StoreSkeleton className="h-5 w-3/4 rounded-md" />
                    <StoreSkeleton className="cart-line-delete h-8 w-8 !bg-luxury-800/40" />
                  </div>
                  <div className="flex items-center gap-2">
                    <StoreSkeleton className="h-3.5 w-16 rounded-md" />
                    <StoreSkeleton className="h-5 w-14 brand-radius-a" />
                  </div>
                  <StoreSkeleton className="cart-line-actions mt-1.5 h-10 w-full !rounded-[0.625rem_0_0.625rem_0]" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <StoreSkeleton className="card-p-md mt-8 hidden h-fit min-h-[16rem] lg:mt-0 lg:block" />
      </div>
    </div>
  )
}

export function SearchPageSkeleton({ withBack = true }: { withBack?: boolean }) {
  return (
    <div className="page-shell" aria-busy aria-label="Loading search">
      <PageHeaderSkeleton withBack={withBack} />
      <StoreSkeleton className="store-input mb-8 h-11 w-full max-w-xl" />
      <ProductGridSkeleton count={6} layout="grid" />
    </div>
  )
}

export function WishlistPageSkeleton() {
  return (
    <div aria-busy aria-label="Loading wishlist">
      <ProductGridSkeleton count={4} />
    </div>
  )
}

/** Matches collapsed order cards on My Orders. */
export function OrderCardSkeleton({ expanded = false }: { expanded?: boolean }) {
  return (
    <div className="orders-card space-y-0" aria-hidden>
      <div className="orders-card-summary">
        <StoreSkeleton className="orders-card-thumb-single" />
        <div className="min-w-0 flex-1 space-y-2">
          <StoreSkeleton className="h-5 w-32 rounded-md" />
          <StoreSkeleton className="h-4 w-40 rounded-md" />
          <StoreSkeleton className="h-5 w-24 rounded-full" />
        </div>
        <div className="space-y-2 text-right">
          <StoreSkeleton className="ml-auto h-6 w-20 rounded-md" />
          <StoreSkeleton className="ml-auto h-4 w-16 rounded-md" />
        </div>
      </div>
      <div className="orders-card-status-row">
        <StoreSkeleton className="h-9 w-9 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2">
          <StoreSkeleton className="h-4 w-36 rounded-md" />
          <StoreSkeleton className="h-3 w-full max-w-xs rounded-md" />
        </div>
        <StoreSkeleton className="h-4 w-24 rounded-md" />
      </div>
      {expanded && (
        <div className="orders-card-details space-y-4">
          <StoreSkeleton className="h-16 w-full brand-radius-a" />
          <StoreSkeleton className="h-32 w-full brand-radius-b" />
        </div>
      )}
    </div>
  )
}

export function OrdersListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-4" aria-busy aria-label="Loading orders">
      <div className="orders-list-header">
        <div className="space-y-2">
          <StoreSkeleton className="h-7 w-36 rounded-md" />
          <StoreSkeleton className="h-4 w-28 rounded-md" />
        </div>
        <StoreSkeleton className="h-9 w-24 brand-radius-a" />
      </div>
      {Array.from({ length: count }).map((_, i) => (
        <OrderCardSkeleton key={i} expanded={i === 0} />
      ))}
      <div className="flex items-center justify-center gap-3 pt-2">
        <StoreSkeleton className="h-9 w-9 rounded-lg" />
        <StoreSkeleton className="h-4 w-20 rounded-md" />
        <StoreSkeleton className="h-9 w-9 rounded-lg" />
      </div>
    </div>
  )
}

export function ProductDetailPageSkeleton() {
  return (
    <div className="store-container pb-16" aria-busy aria-label="Loading product">
      <PageHeaderSkeleton withSubtitle={false} />
      <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
        <div className="space-y-4">
          <StoreSkeleton className="product-detail-gallery-main w-full min-h-[18rem] lg:min-h-[26rem]" />
          <div className="flex gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <StoreSkeleton key={i} className="h-[4.25rem] w-[4.25rem] shrink-0 rounded-lg" />
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div className="flex gap-2">
            <StoreSkeleton className="h-6 w-16 rounded-md" />
            <StoreSkeleton className="h-6 w-20 rounded-full" />
          </div>
          <StoreSkeleton className="h-10 w-4/5 max-w-md rounded-lg" />
          <StoreSkeleton className="h-5 w-32 rounded-md" />
          <StoreSkeleton className="h-4 w-2/3 max-w-sm rounded-md" />
          <div className="product-detail-purchase-card space-y-4 p-5">
            <div className="flex items-end justify-between">
              <StoreSkeleton className="h-10 w-28 rounded-md" />
              <StoreSkeleton className="h-9 w-24 rounded-lg" />
            </div>
            <div className="flex gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <StoreSkeleton key={i} className="h-9 w-14 rounded-lg" />
              ))}
            </div>
            <div className="flex gap-2">
              <StoreSkeleton className="h-12 flex-1 rounded-xl" />
              <StoreSkeleton className="h-12 flex-1 rounded-xl" />
              <StoreSkeleton className="h-12 w-12 rounded-xl" />
            </div>
          </div>
          <div className="product-detail-section space-y-2 p-5">
            <StoreSkeleton className="h-5 w-36 rounded-md" />
            <StoreSkeleton className="h-3 w-full rounded-md" />
            <StoreSkeleton className="h-3 w-full rounded-md" />
            <StoreSkeleton className="h-3 w-5/6 rounded-md" />
          </div>
        </div>
      </div>
    </div>
  )
}

export function StoreLegalPageSkeleton() {
  return (
    <div className="space-y-6" aria-busy aria-label="Loading policy">
      {Array.from({ length: 6 }).map((_, i) => (
        <StoreSkeleton
          key={i}
          className={cn(
            'h-3 rounded-md',
            i % 3 === 2 ? 'w-4/5' : 'w-full'
          )}
        />
      ))}
      <StoreSkeleton className="h-3 w-2/3 rounded-md" />
      <div className="pt-4 space-y-3">
        <StoreSkeleton className="h-4 w-40 rounded-md" />
        {Array.from({ length: 4 }).map((_, i) => (
          <StoreSkeleton key={i} className="h-3 w-full rounded-md" />
        ))}
      </div>
    </div>
  )
}

export function StoreOrderDetailPageSkeleton() {
  return (
    <div className="page-shell" aria-busy aria-label="Loading order">
      <StoreSkeleton className="h-4 w-32 rounded-md mb-6" />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="space-y-2">
          <StoreSkeleton className="h-9 w-44 rounded-lg" />
          <StoreSkeleton className="h-4 w-36 rounded-md" />
        </div>
        <StoreSkeleton className="h-7 w-24 rounded-full" />
      </div>
      <div className="glass-card gold-border-glow p-6 mb-6 space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <StoreSkeleton key={i} className="h-4 w-full max-w-lg rounded-md" />
        ))}
      </div>
      <div className="glass-card gold-border-glow p-6 space-y-4">
        <StoreSkeleton className="h-6 w-36 rounded-md" />
        <div className="rounded-xl border border-luxury-800 bg-luxury-950/50 p-4 space-y-3">
          <StoreSkeleton className="h-4 w-16 rounded-md" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex justify-between gap-4">
              <StoreSkeleton className="h-4 flex-1 rounded-md" />
              <StoreSkeleton className="h-4 w-16 rounded-md" />
            </div>
          ))}
          <StoreSkeleton className="h-5 w-32 rounded-md ml-auto" />
        </div>
      </div>
    </div>
  )
}

export function HomeBannersSkeleton() {
  return (
    <section className="relative overflow-hidden bg-[#FAF8F4]" aria-hidden>
      <div className="store-container relative pb-5 pt-0 md:pb-8">
        <div className="mb-4 md:mb-5">
          <StoreSkeleton className="h-4 w-24 rounded-md" />
        </div>
        <div className="mb-4 space-y-2 md:mb-5">
          <StoreSkeleton className="h-8 w-64 max-w-full rounded-lg md:h-9" />
          <StoreSkeleton className="h-5 w-full max-w-xl rounded-md" />
        </div>
        <div className="gold-gradient-ring store-banner-frame p-[2.5px] ring-1 ring-gold-500/20">
          <div className="store-banner-stage w-full">
            <StoreSkeleton className="store-banner-media w-full" />
          </div>
        </div>
      </div>
    </section>
  )
}

export function HomeHeroSkeleton() {
  return (
    <section className="relative overflow-hidden" aria-hidden>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-gold-100/30 via-luxury-950 to-luxury-950" />
      <div className="store-container relative py-14 md:py-20 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="space-y-5">
            <StoreSkeleton className="h-8 w-64 max-w-full rounded-full" />
            <StoreSkeleton className="h-14 md:h-20 w-full max-w-xl rounded-xl" />
            <StoreSkeleton className="h-5 w-full max-w-lg rounded-md" />
            <StoreSkeleton className="h-5 w-4/5 max-w-md rounded-md" />
            <div className="flex flex-wrap gap-3 pt-2">
              <StoreSkeleton className="h-12 w-44 rounded-xl" />
              <StoreSkeleton className="h-12 w-40 rounded-xl" />
            </div>
            <div className="flex gap-3 pt-4">
              <StoreSkeleton className="h-8 w-32 rounded-full" />
              <StoreSkeleton className="h-8 w-36 rounded-full" />
            </div>
          </div>
          <div className="w-full">
            <StoreSkeleton className="w-full aspect-[4/3] sm:aspect-[16/10] rounded-2xl" />
          </div>
        </div>
      </div>
    </section>
  )
}

export function NotesPageSkeleton() {
  return (
    <div className="flex flex-wrap gap-4 sm:gap-5 md:gap-6" aria-hidden>
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="flex w-[168px] flex-col items-center gap-3 sm:w-[172px] md:w-[188px]">
          <StoreSkeleton
            className={cn(
              'h-[160px] w-[160px] sm:h-[164px] sm:w-[164px] md:h-[180px] md:w-[180px]',
              i % 2 === 0 ? 'brand-radius-a' : 'brand-radius-b'
            )}
          />
          <StoreSkeleton className="h-3 w-14 rounded-md" />
        </div>
      ))}
    </div>
  )
}

export function HomeFragranceNotesSkeleton() {
  return (
    <section className="relative overflow-hidden bg-white" aria-hidden>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#F3EFE8] via-white to-[#FAF8F4]" />
      <div className="store-container relative py-8 md:py-10">
        <div className="mb-5 space-y-2 md:mb-6">
          <StoreSkeleton className="h-3 w-16 rounded-md" />
          <StoreSkeleton className="h-6 w-56 rounded-md" />
        </div>
        <div className="flex gap-4 overflow-hidden sm:gap-5 md:gap-6">
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              key={i}
              className="flex w-[168px] shrink-0 flex-col items-center gap-3 sm:w-[172px] md:w-[188px]"
            >
              <StoreSkeleton
                className={cn(
                  'h-[160px] w-[160px] sm:h-[164px] sm:w-[164px] md:h-[180px] md:w-[180px]',
                  i % 2 === 0 ? 'brand-radius-a' : 'brand-radius-b'
                )}
              />
              <StoreSkeleton className="h-3 w-14 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export function HomeCategoriesSkeleton() {
  return (
    <section className="relative overflow-hidden bg-[#FAF8F4]" aria-hidden>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-[#FAF8F4] to-[#FAF8F4]" />
      <div className="store-container relative pb-4 pt-6 md:pb-5 md:pt-8">
        <div className="mb-4 space-y-2 md:mb-5">
          <StoreSkeleton className="h-3 w-16 rounded-md" />
          <StoreSkeleton className="h-6 w-40 rounded-md" />
        </div>
        <div className="flex gap-4 overflow-hidden sm:gap-5">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="flex w-[120px] shrink-0 flex-col items-center gap-2 sm:w-[142px]">
              <StoreSkeleton className="h-[120px] w-[120px] rounded-full sm:h-[142px] sm:w-[142px]" />
              <StoreSkeleton className="h-3 w-12 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export function HomeStorySkeleton() {
  return (
    <section className="section-shell" aria-hidden>
      <StoreSkeleton className="h-8 w-48 rounded-lg mb-10" />
      <StoreSkeleton className="h-64 md:h-72 w-full rounded-3xl" />
    </section>
  )
}

/** Checkout contact / delivery details while persisted form hydrates. */
export function CheckoutDetailsSkeleton() {
  return (
    <div className="space-y-6" aria-busy aria-label="Loading your details">
      <div className="glass-card gold-border-glow p-6 space-y-4">
        <StoreSkeleton className="h-6 w-40 rounded-md" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
      </div>
      <div className="glass-card gold-border-glow p-6 space-y-4">
        <StoreSkeleton className="h-6 w-44 rounded-md" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-2/3 rounded-xl" />
      </div>
    </div>
  )
}

/** Full home page placeholder — hero, categories, products, and story. */
export function HomePageSkeleton() {
  return (
    <div aria-busy aria-label="Loading home page">
      <HomeCategoriesSkeleton />
      <HomeBannersSkeleton />
      <HomeProductSectionSkeleton />
      <HomeProductSectionSkeleton />
    </div>
  )
}

export function HomeProductSectionSkeleton({ cols = 4 }: { cols?: number }) {
  return (
    <section className="section-shell-sm" aria-hidden>
      <SectionHeadingSkeleton />
      <ProductGridSkeleton count={cols} />
    </section>
  )
}
